/**
 * Relevo de verdade (techspec-relevo): o terreno esticado do cliente do UO e o passo 3D.
 *
 * Só o mapa que traz `relevo` no JSON usa isto. Os outros mapas não passam por aqui: o desenho e o passo deles são os
 * de sempre (`blocos.ts`, `montarWalk`).
 *
 * - **Passo:** o empacotador (tools/relevo_mapa.py, a regra do ServUO) já grava, por casa, a altura do pé (`zpe`),
 *   se dá para ficar em pé (`anda`) e os 8 passos permitidos (`passos`). Cliente e servidor (Maps/WalkGrid.cs) só leem.
 * - **Desenho:** cada casa é rasterizada pixel a pixel. Com os quatro cantos no mesmo z vale a arte 44×44; com cantos
 *   diferentes, o texmap esticado. A luz é por vértice (a normal sai dos vizinhos) e interpolada, sem dobra entre as
 *   casas. A regra de preenchimento deixa o pixel da aresta com uma vizinha só: as casas encaixam sem fresta.
 */

/** A ordem dos bits de `passos`. Igual em tools/relevo_mapa.py e backend Maps/WalkGrid.cs. */
export const DIRS8: readonly (readonly [number, number])[] = [
  [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1]
];

export function bitDoPasso(dx: number, dy: number): number {
  for (let i = 0; i < 8; i++) if (DIRS8[i][0] === dx && DIRS8[i][1] === dy) return i;
  return -1;
}

export interface RelevoJson {
  readonly formato: number;
  readonly zpe: string;
  readonly anda: string;
  readonly passos: string;
  readonly cobre: string;
  readonly luz?: number;
}

/** O que o desenho precisa de cada terreno: a arte plana e o texmap (lado em px). */
export interface TerrenoDoRelevo {
  readonly x: number;
  readonly y: number;
  readonly tex?: readonly [number, number, number];
  /** Usa o texmap mesmo plana: o vazio coberto de neve (`vazio = neve`), que tem a arte de outro tom. */
  readonly texSempre?: boolean;
}

export interface CasaRaster {
  readonly c: HTMLCanvasElement;
  /** Onde fica o canto de cima do losango dentro do canvas. */
  readonly ox: number;
  readonly oy: number;
}

export interface RelevoMapa {
  readonly zMin: number;
  readonly zMax: number;
  /** Fica em pé na casa (ligada ao spawn). */
  anda(x: number, y: number): boolean;
  /** A altura do pé na casa. Fora do andável, o z médio do terreno. */
  zPe(x: number, y: number): number;
  /** O z do canto de cima da casa (o do terreno; fora do mapa repete a borda). */
  zCanto(x: number, y: number): number;
  /** O passo (dx, dy) saindo de (x, y). */
  passo(x: number, y: number, dx: number, dy: number): boolean;
  /** O z médio da casa (Map.GetAverageZ do ServUO). */
  zMedio(x: number, y: number): number;
  /** Parede da caverna que tampa quem anda atrás dela. */
  cobreNoBloco(bx: number, by: number): readonly { x: number; y: number }[];
  /** A casa pronta para desenhar (cacheada), ou null se não há o que desenhar. */
  casa(x: number, y: number): CasaRaster | null;
}

function debase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const cl = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
const KZ = 0.09;
const LUZ = (() => { const v = [-0.65, -0.2, 1]; const n = Math.hypot(v[0], v[1], v[2]); return v.map(c => c / n); })();
const LUZ_MIN = 0.78, LUZ_MAX = 1.1;
const CACHE_MAX = 6000;

export function montarRelevo(
  raw: RelevoJson,
  largura: number,
  altura: number,
  zBuf: Uint8Array,
  terreno: Uint8Array,
  terrenos: readonly TerrenoDoRelevo[],
  imagem: HTMLCanvasElement,
  bloco: number,
  rotulo: string
): RelevoMapa {
  if (raw.formato !== 1) throw new Error(`${rotulo}: relevo formato ${raw.formato} recusado.`);
  const n = largura * altura;
  const zpe = debase64(raw.zpe), anda = debase64(raw.anda), passos = debase64(raw.passos), cobre = debase64(raw.cobre);
  for (const [nome, b] of [['zpe', zpe], ['anda', anda], ['passos', passos], ['cobre', cobre]] as const)
    if (b.length !== n) throw new Error(`${rotulo}: relevo.${nome} tem ${b.length} bytes, esperado ${n}.`);
  const forca = cl(Number(raw.luz ?? 0.4), 0, 1);

  const idx = (x: number, y: number) => y * largura + x;
  const zCanto = (x: number, y: number) => zBuf[idx(cl(x, 0, largura - 1), cl(y, 0, altura - 1))] - 128;
  let zMin = 127, zMax = -128;
  for (let i = 0; i < n; i++) { const z = zBuf[i] - 128; if (z < zMin) zMin = z; if (z > zMax) zMax = z; }

  const floorAvg = (a: number, b: number) => { let v = a + b; if (v < 0) v--; return Math.trunc(v / 2); };
  const zMedio = (x: number, y: number) => {
    const zt = zCanto(x, y), zl = zCanto(x, y + 1), zr = zCanto(x + 1, y), zb = zCanto(x + 1, y + 1);
    return Math.abs(zt - zb) > Math.abs(zl - zr) ? floorAvg(zl, zr) : floorAvg(zt, zb);
  };

  // A luz de cada canto da grade (largura+1 × altura+1).
  const L = largura + 1;
  const luz = new Float32Array(L * (altura + 1));
  for (let j = 0; j <= altura; j++) for (let i = 0; i <= largura; i++) {
    const gx = (zCanto(i + 1, j) - zCanto(i - 1, j)) / 2 * KZ, gy = (zCanto(i, j + 1) - zCanto(i, j - 1)) / 2 * KZ;
    const nn = Math.hypot(gx, gy, 1);
    const f = (-gx * LUZ[0] - gy * LUZ[1] + LUZ[2]) / nn / LUZ[2];
    luz[j * L + i] = cl(1 + (f - 1) * forca, LUZ_MIN, LUZ_MAX);
  }
  const luzEm = (i: number, j: number) => luz[cl(j, 0, altura) * L + cl(i, 0, largura)];

  // Paredes que tampam, por bloco.
  const blocosX = Math.ceil(largura / bloco);
  const cobrePorBloco = new Map<number, { x: number; y: number }[]>();
  for (let y = 0; y < altura; y++) for (let x = 0; x < largura; x++) {
    if (!cobre[idx(x, y)]) continue;
    const k = Math.floor(y / bloco) * blocosX + Math.floor(x / bloco);
    const l = cobrePorBloco.get(k);
    if (l) l.push({ x, y }); else cobrePorBloco.set(k, [{ x, y }]);
  }

  let pix: Uint8ClampedArray | null = null;
  const pixels = () => {
    if (pix) return pix;
    const g = imagem.getContext('2d', { willReadFrequently: true });
    if (!g) throw new Error(`${rotulo}: atlas sem contexto 2D.`);
    pix = g.getImageData(0, 0, imagem.width, imagem.height).data;
    return pix;
  };
  const larAtlas = imagem.width;

  const cache = new Map<string, CasaRaster>();
  /** Lado da aresta (a→b) que fica com o pixel em cima dela: uma das duas casas vizinhas, nunca as duas. */
  const dono = (ax: number, ay: number, bx: number, by: number) => (by - ay) > 0 || ((by - ay) === 0 && (bx - ax) < 0);
  const ed = (ax: number, ay: number, bx: number, by: number, px: number, py: number) =>
    (bx - ax) * (py - ay) - (by - ay) * (px - ax);

  function rasterizar(t: number, zt: number, zr: number, zb: number, zl: number,
    bT: number, bR: number, bB: number, bL: number): CasaRaster | null {
    const def = terrenos[t];
    if (!def) return null;
    const plana = zt === zr && zt === zb && zt === zl;
    const tex = !plana || def.texSempre ? def.tex : undefined;
    const yT = 0, yR = 22 - (zr - zt) * 4, yB = 44 - (zb - zt) * 4, yL = 22 - (zl - zt) * 4;
    const minY = Math.min(yT, yR, yB, yL), maxY = Math.max(yT, yR, yB, yL);
    const w = 44, h = Math.max(1, maxY - minY);
    const Tx = 22, Ty = yT - minY, Rx = 44, Ry = yR - minY, Bx = 22, By = yB - minY, Lx = 0, Ly = yL - minY;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const g = c.getContext('2d');
    if (!g) return null;
    const img = g.createImageData(w, h), o = img.data;
    const fonte = pixels();
    const lado = tex ? tex[2] : 44, ult = lado - 1;
    const ox0 = tex ? tex[0] : def.x, oy0 = tex ? tex[1] : def.y;
    const pinta = (u: number, v: number, l: number, i: number) => {
      let R: number, G: number, B: number;
      if (tex) {
        u = cl(u - 0.5, 0, ult); v = cl(v - 0.5, 0, ult);
        const u0 = u | 0, v0 = v | 0, u1 = Math.min(u0 + 1, ult), v1 = Math.min(v0 + 1, ult), fu = u - u0, fv = v - v0;
        const p00 = ((oy0 + v0) * larAtlas + ox0 + u0) * 4, p10 = ((oy0 + v0) * larAtlas + ox0 + u1) * 4;
        const p01 = ((oy0 + v1) * larAtlas + ox0 + u0) * 4, p11 = ((oy0 + v1) * larAtlas + ox0 + u1) * 4;
        const mix = (k: number) => (fonte[p00 + k] * (1 - fu) + fonte[p10 + k] * fu) * (1 - fv)
          + (fonte[p01 + k] * (1 - fu) + fonte[p11 + k] * fu) * fv;
        R = mix(0); G = mix(1); B = mix(2);
      } else {
        const p = ((oy0 + cl(v | 0, 0, 43)) * larAtlas + ox0 + cl(u | 0, 0, 43)) * 4;
        if (fonte[p + 3] === 0) return;
        R = fonte[p]; G = fonte[p + 1]; B = fonte[p + 2];
      }
      o[i] = R * l; o[i + 1] = G * l; o[i + 2] = B * l; o[i + 3] = 255;
    };
    const a1 = ed(Tx, Ty, Rx, Ry, Bx, By), a2 = ed(Tx, Ty, Bx, By, Lx, Ly);
    for (let py = 0; py < h; py++) {
      const cy = py + 0.5;
      for (let px = 0; px < w; px++) {
        const cx = px + 0.5, i = (py * w + px) * 4;
        if (a1 > 0) {
          const wT = ed(Rx, Ry, Bx, By, cx, cy), wR = ed(Bx, By, Tx, Ty, cx, cy), wB = ed(Tx, Ty, Rx, Ry, cx, cy);
          if ((wT > 0 || (wT === 0 && dono(Rx, Ry, Bx, By))) && (wR > 0 || (wR === 0 && dono(Bx, By, Tx, Ty)))
            && (wB > 0 || (wB === 0 && dono(Tx, Ty, Rx, Ry)))) {
            const lT = wT / a1, lR = wR / a1, lB = wB / a1;
            const l = lT * bT + lR * bR + lB * bB;
            if (tex) pinta(lado * (lR + lB), lado * lB, l, i); else pinta(px, py + minY, l, i);
            continue;
          }
        }
        if (a2 > 0) {
          const wT = ed(Bx, By, Lx, Ly, cx, cy), wB = ed(Lx, Ly, Tx, Ty, cx, cy), wL = ed(Tx, Ty, Bx, By, cx, cy);
          if ((wT > 0 || (wT === 0 && dono(Bx, By, Lx, Ly))) && (wB > 0 || (wB === 0 && dono(Lx, Ly, Tx, Ty)))
            && (wL > 0 || (wL === 0 && dono(Tx, Ty, Bx, By)))) {
            const lT = wT / a2, lB = wB / a2, lL = wL / a2;
            const l = lT * bT + lB * bB + lL * bL;
            if (tex) pinta(lado * lB, lado * (lB + lL), l, i); else pinta(px, py + minY, l, i);
          }
        }
      }
    }
    g.putImageData(img, 0, 0);
    return { c, ox: 22, oy: -minY };
  }

  const q = (v: number) => Math.round(v * 48);
  return {
    zMin, zMax,
    anda: (x, y) => x >= 0 && y >= 0 && x < largura && y < altura && anda[idx(x, y)] === 1,
    zPe: (x, y) => (x >= 0 && y >= 0 && x < largura && y < altura ? zpe[idx(x, y)] - 128 : 0),
    zCanto,
    zMedio,
    passo(x, y, dx, dy) {
      if (x < 0 || y < 0 || x >= largura || y >= altura) return false;
      const b = bitDoPasso(dx, dy);
      return b >= 0 && (passos[idx(x, y)] & (1 << b)) !== 0;
    },
    cobreNoBloco: (bx, by) => cobrePorBloco.get(by * blocosX + bx) ?? [],
    casa(x, y) {
      if (x < 0 || y < 0 || x >= largura || y >= altura) return null;
      const t = terreno[idx(x, y)];
      const zt = zCanto(x, y), zr = zCanto(x + 1, y), zb = zCanto(x + 1, y + 1), zl = zCanto(x, y + 1);
      const bT = luzEm(x, y), bR = luzEm(x + 1, y), bB = luzEm(x + 1, y + 1), bL = luzEm(x, y + 1);
      const k = `${t}|${zr - zt}|${zb - zt}|${zl - zt}|${q(bT)}|${q(bR)}|${q(bB)}|${q(bL)}`;
      const hit = cache.get(k);
      if (hit) return hit;
      const r = rasterizar(t, zt, zr, zb, zl, bT, bR, bB, bL);
      if (!r) return null;
      if (cache.size >= CACHE_MAX) cache.clear();
      cache.set(k, r);
      return r;
    }
  };
}
